/**
 * Consent Initialization Hook
 * Manages conditional loading of tracking scripts based on user consent
 */

import { useEffect } from 'react';
import { getConsent, hasConsent } from '../utils/consentManager';
import { initClarity, disableClarity } from '../utils/clarity';
import { initializeFacebookPixel, revokeFacebookPixelConsent } from '../utils/facebookPixel';
import { initializeGoogleAnalytics, disableGoogleAnalytics, updateGAConsent } from '../utils/googleAnalytics';
import { clearTrackingCookies } from '../utils/consentManager';

/**
 * Initialize tracking scripts based on stored consent preferences
 * This hook should be called once when the app mounts
 */
export const useConsentInitialization = (): void => {
  useEffect(() => {
    const preferences = getConsent();

    console.log('Initializing tracking services based on consent:', preferences);

    // Initialize analytics services if consent given
    if (preferences.analytics) {
      initializeGoogleAnalytics();
      initClarity();
    } else {
      // Ensure analytics are disabled
      disableGoogleAnalytics();
      disableClarity();
      clearTrackingCookies('analytics');
    }

    // Initialize marketing services if consent given
    if (preferences.marketing) {
      initializeFacebookPixel();
    } else {
      // Ensure marketing tracking is disabled
      revokeFacebookPixelConsent();
      clearTrackingCookies('marketing');
    }

    // Listen for consent changes and dynamically load/unload scripts
    const handleConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newPreferences = customEvent.detail;

      console.log('Consent preferences changed:', newPreferences);

      // Handle analytics consent changes
      if (newPreferences.analytics && !preferences.analytics) {
        // Analytics just enabled
        initializeGoogleAnalytics();
        initClarity();
      } else if (!newPreferences.analytics && preferences.analytics) {
        // Analytics just disabled
        disableGoogleAnalytics();
        disableClarity();
        clearTrackingCookies('analytics');
      }

      // Handle marketing consent changes
      if (newPreferences.marketing && !preferences.marketing) {
        // Marketing just enabled
        initializeFacebookPixel();
      } else if (!newPreferences.marketing && preferences.marketing) {
        // Marketing just disabled
        revokeFacebookPixelConsent();
        clearTrackingCookies('marketing');
      }

      // Update Google Analytics consent mode
      updateGAConsent(newPreferences.analytics, newPreferences.marketing);

      // Update stored preferences reference for next comparison
      preferences.analytics = newPreferences.analytics;
      preferences.marketing = newPreferences.marketing;
    };

    // Listen for consent change events
    window.addEventListener('consentChanged', handleConsentChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('consentChanged', handleConsentChange);
    };
  }, []); // Run once on mount
};

/**
 * Hook to check if a specific consent category is granted
 * Useful for conditional rendering based on consent
 *
 * @example
 * const hasAnalytics = useConsentCheck('analytics');
 * if (hasAnalytics) {
 *   // Render analytics-related UI
 * }
 */
export const useConsentCheck = (category: 'essential' | 'analytics' | 'marketing'): boolean => {
  return hasConsent(category);
};
